import { toast } from "@carbon6/c6-frontend-design";
import { useEffect, useState } from "react";
import {
  addBookmark,
  archiveDataset,
  designateDatasetVideoFps,
  getDataset,
  getDatasetDetails,
  getDatasetStructure,
  refreshDatasetDetails as refreshDatasetDetailsApi,
  restoreDataset,
  type Dataset,
  type DatasetDetails,
  type DatasetStructure,
} from "@/api";
import { useAppState } from "@/app";
import { isConflictError } from "@/http";
import { createLogger } from "@/logging";
import { addDatasetUuid, includesDatasetUuid, removeDatasetUuid } from "@/utils";
import type { LoadState } from "@/components/dataset/loadState";
import type { DatasetReviewOutcome } from "@/components/dataset/review/DatasetReviewPanel";
import {
  useDatasetReview,
  type DatasetReviewMode,
} from "@/components/dataset/review/useDatasetReview";
import { useAnnotationStudio } from "@/page/annotationStudio/useAnnotationStudio";
import type { DatasetDetailTab } from "./datasetDetailTabs";
import { resolveErrorMessage } from "./datasetDetailFormatters";
import { supportsStorageFeature, type DatasetStorageBackend } from "@/api/datasetStorageBackend";

const logger = createLogger("page/datasetDetail");

export type DatasetDetailControllerOptions = {
  // 데이터셋 리뷰 탭이 지금 활성인지. 탭을 열기 전에는 configuration pair 를 조회하지 않는다
  // (지연 로드).
  reviewEnabled?: boolean;
  // 리뷰 탭의 동작. direct=저장(PUT) · candidate=후보 확정/폐기 + 검토 완료.
  reviewMode?: DatasetReviewMode;
  // 어노테이션 탭이 지금 활성인지. 처음 켜질 때 스튜디오 init 을 래치한다. 리뷰와 다르게 탭을
  // 벗어나도 dispose 하지 않는다. 그래서 draft 가 유지된다(정리는 창/페이지 언마운트 때 한다).
  annotationEnabled?: boolean;
  // 탭 전환 요청(개요의 리뷰 진입점). 탭 상태는 호스트(창·페이지)가 소유한다.
  onRequestTab?: (tab: DatasetDetailTab) => void;
  // 리뷰 액션 뒤 호스트별 후속 동작(예: 창 닫기). 상세 재조회는 controller 가 이미 처리한다.
  onReviewOutcome?: (outcome: DatasetReviewOutcome) => void;
};

// 데이터셋 상세의 데이터 적재 + 액션 상태/핸들러를 한곳에서 소유하는 controller. 라우트 페이지와
// 전체화면 모달이 각각 1회 호출한다. 그래서 제목·헤더 액션·본문이 모두 같은 상태를 공유한다.
// 액션 실패는 inline 텍스트 대신 toast 로 알린다(버튼이 모든 탭에서 헤더에 있기 때문이다).
// 이 controller 에는 다른 화면으로 navigate 하는 액션이 없다. 창을 닫는 책임은 창 셸이 가진다.
export function useDatasetDetailController(
  datasetUuid: string,
  options: DatasetDetailControllerOptions = {},
) {
  const { state: appState } = useAppState();
  const currentUserRole = appState.currentUser?.role ?? null;
  const [dataset, setDataset] = useState<LoadState<Dataset | null>>({ status: "idle" });
  const [datasetDetails, setDatasetDetails] = useState<LoadState<DatasetDetails>>({
    status: "idle",
  });
  // 채널 구조(공용 structure 엔드포인트). 상세 개요의 채널 구조 카드 소스다. 실패하면 카드가
  // 알림 없이 degrade 한다. 그래서 화면 에러로 올리지 않는다(개요의 다른 카드와 독립).
  const [structure, setStructure] = useState<LoadState<DatasetStructure>>({
    status: "idle",
  });
  const currentDataset = dataset.status === "success" ? dataset.data : null;
  // 별(북마크 담기) 액션의 낙관적 상태. 담긴 uuid 와 요청 중인 uuid.
  const [bookmarkUuids, setBookmarkUuids] = useState<readonly string[]>([]);
  const [addingDatasetUuids, setAddingDatasetUuids] = useState<readonly string[]>([]);
  const [archiving, setArchiving] = useState(false);
  // 값을 증가시키면 dataset·details 를 다시 불러온다(라벨/embodiment 편집 반영용).
  const [refreshKey, setRefreshKey] = useState(0);
  // 마지막으로 받은 저장 위치. Dataset 을 다시 받는 동안에도 남는다. uuid 를 함께 둬서 다른 Dataset 의
  // 값이 섞이지 않는다.
  const [knownStorageBackend, setKnownStorageBackend] = useState<{
    datasetUuid: string;
    backend: DatasetStorageBackend;
  } | null>(null);
  // archived 복구(Activate). 라벨 없는 단순 확인 모달. 예약 라벨/embodiment 게이트는 리뷰가
  // 소유한다. reviewed dataset 만 이 경로에 도달한다. 그래서 여기서는 확인 후 restore 만 한다.
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // 데이터셋 리뷰(embodiment·tip·label 확정) 바인딩. 리뷰 탭이 열린 뒤에만 조회한다. draft·dirty
  // 는 이 바인딩이 소유한다. 이탈 가드는 호스트가 review.snapshot.dirty 를 읽어 적용한다.
  const review = useDatasetReview(
    datasetUuid,
    options.reviewMode ?? "direct",
    options.reviewEnabled ?? false,
  );

  // Annotation 은 저장 위치가 지원할 때만 연다. 판정에는 Dataset 응답이 필요하다. 그래서 첫 응답 전에는
  // 스튜디오를 켜지 않는다. 지원하지 않는 위치에서 세트를 조회하면 409 다. 새로고침 중에는 Dataset 이
  // 잠시 비지만 직전 판정을 유지한다. 판정이 흔들리면 스튜디오가 저장된 프레임을 다시 읽는다.
  const annotationSupported =
    knownStorageBackend?.datasetUuid === datasetUuid
      ? supportsStorageFeature(knownStorageBackend.backend, "annotation")
      : null;

  // SAM 어노테이션 스튜디오 바인딩. 어노테이션 탭이 사용한다.
  const annotationStudio = useAnnotationStudio(datasetUuid, {
    enabled: (options.annotationEnabled ?? false) && annotationSupported === true,
    collectionLabel: currentDataset?.display_name ?? datasetUuid,
    totalSequenceCount: currentDataset?.total_episodes ?? 0,
    cameras:
      datasetDetails.status === "success" ? datasetDetails.data.camera_stream_keys : undefined,
  });

  useEffect(() => {
    setArchiving(false);
    setRestoreConfirmOpen(false);
    setRestoring(false);
  }, [datasetUuid]);

  useEffect(() => {
    if (!datasetUuid) {
      setDataset({ status: "error", message: "Dataset ID가 없습니다." });
      return;
    }
    let cancelled = false;
    setDataset({ status: "loading" });
    void loadDataset(datasetUuid)
      .then((data) => {
        if (cancelled) return;
        setDataset({ status: "success", data });
        if (data !== null) {
          setKnownStorageBackend({ datasetUuid, backend: data.storage_backend });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error("loadDataset failed", error);
        setDataset({ status: "error", message: resolveErrorMessage(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [datasetUuid, refreshKey]);

  useEffect(() => {
    if (!datasetUuid) {
      setDatasetDetails({ status: "error", message: "Dataset ID가 없습니다." });
      return;
    }
    let cancelled = false;
    setDatasetDetails({ status: "loading" });
    void getDatasetDetails(datasetUuid)
      .then((data) => {
        if (!cancelled) setDatasetDetails({ status: "success", data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error("getDatasetDetails failed", error);
        setDatasetDetails({ status: "error", message: resolveErrorMessage(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [datasetUuid, refreshKey]);

  useEffect(() => {
    if (!datasetUuid) {
      setStructure({ status: "error", message: "Dataset ID가 없습니다." });
      return;
    }
    let cancelled = false;
    setStructure({ status: "loading" });
    void getDatasetStructure(datasetUuid)
      .then((data) => {
        if (!cancelled) setStructure({ status: "success", data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error("getDatasetStructure failed", error);
        setStructure({ status: "error", message: resolveErrorMessage(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [datasetUuid, refreshKey]);

  // 상세 새로고침. summary 재집계(details)와 raw Dataset 재조회를 함께 한다. 파생 상태
  // (`processing`)에는 폴링이 없다. 그래서 이 액션이 유일한 갱신 경로다. 둘을 분리하면 사용자가
  // 새로고침을 눌러도 진행 중 배지가 그대로 남는다. dataset 재조회 실패는 화면 에러로 올리지
  // 않는다. 재집계 결과는 이미 받았다. 표시된 상태가 한 번 더 오래된 값으로 남을 뿐이다.
  function handleDatasetDetailsRefresh() {
    if (!datasetUuid) return;
    setDatasetDetails({ status: "loading" });
    void refreshDatasetDetailsApi(datasetUuid)
      .then((data) => {
        setDatasetDetails({ status: "success", data });
      })
      .catch((error: unknown) => {
        logger.error("refreshDatasetDetails failed", error);
        setDatasetDetails({ status: "error", message: resolveErrorMessage(error) });
      });
    void getDataset(datasetUuid)
      .then((data) => {
        setDataset({ status: "success", data });
      })
      .catch((error: unknown) => {
        logger.error("refresh dataset failed", error);
      });
  }

  // 별을 누르면 북마크(me/bookmarks)에 담는다. 409(이미 담김)는 성공으로 처리해 별을 채운다.
  async function handleAddBookmarkClick(target: Dataset) {
    const uuid = target.uuid;
    if (includesDatasetUuid(bookmarkUuids, uuid) || includesDatasetUuid(addingDatasetUuids, uuid)) {
      return;
    }

    setAddingDatasetUuids((current) => addDatasetUuid(current, uuid));
    try {
      await addBookmark(uuid);
      markAdded(uuid);
    } catch (error) {
      if (isConflictError(error)) {
        markAdded(uuid);
        return;
      }
      logger.error("add bookmark failed", error);
      setAddingDatasetUuids((current) => removeDatasetUuid(current, uuid));
      toast.error(resolveErrorMessage(error));
    }
  }

  function markAdded(uuid: string): void {
    setBookmarkUuids((current) => addDatasetUuid(current, uuid));
    setAddingDatasetUuids((current) => removeDatasetUuid(current, uuid));
  }

  // archive↔restore 토글. 복구(archived→)는 라벨 없는 확인 모달을 연다. 비활성화
  // (available→archived)만 직접 처리한다. archive 결과는 헤더 버튼·배지에 즉시 반영한다.
  async function handleArchiveToggleClick(target: Dataset) {
    if (target.status === "archived") {
      setRestoreConfirmOpen(true);
      return;
    }
    if (archiving) {
      return;
    }
    setArchiving(true);
    try {
      const updated = await archiveDataset(target.uuid);
      setDataset({ status: "success", data: updated });
      setArchiving(false);
    } catch (error) {
      logger.error("dataset archive failed", error);
      toast.error(resolveErrorMessage(error));
      setArchiving(false);
    }
  }

  // 복구 확인 → restore. 리뷰는 노출·사용을 막지 않는다. 그래서 catalog status 가
  // available/archived 면 리뷰 상태와 관계없이 성공한다. 실패는 status 위반(409)뿐이다. 그래서
  // toast 로만 알린다.
  async function confirmRestore() {
    if (restoring || !currentDataset) {
      return;
    }
    const targetUuid = currentDataset.uuid;
    setRestoring(true);
    try {
      const updated = await restoreDataset(targetUuid);
      setDataset({ status: "success", data: updated });
      setRestoreConfirmOpen(false);
      setRestoring(false);
      // status·라벨이 바뀌었다. 그래서 details 도 다시 적재한다.
      setRefreshKey((current) => current + 1);
    } catch (error) {
      setRestoring(false);
      logger.error("dataset restore failed", error);
      toast.error(resolveErrorMessage(error));
    }
  }

  /**
   * 리뷰에서 비어 있는 video fps 를 지정한다.
   *
   * 서버는 빈 행만 채운다. 이미 값이 있는 파일은 바뀌지 않는다. 그래서 결과를 "채웠다" 가
   * 아니라 **몇 개를 채웠는지**로 알린다. 0 은 실패가 아니라 채울 것이 없었다는 뜻이다. 화면이
   * 오래되어 컨트롤이 남아 있었던 경우도 같은 문장으로 알린다.
   *
   * 파생값(`primary_rate_hz`)도 함께 바뀐다. 그래서 상세를 다시 조회한다. 지정 컨트롤을 숨기는
   * 근거도 그 값이다. 실패는 toast 로만 알린다. 입력값은 카드가 보관한다.
   */
  async function designateVideoFps(fps: number): Promise<void> {
    try {
      const result = await designateDatasetVideoFps(datasetUuid, fps);
      setRefreshKey((current) => current + 1);
      toast.success(
        result.filled_file_count > 0
          ? `영상 파일 ${result.filled_file_count}개에 ${result.fps} fps 를 채웠습니다.`
          : "fps 가 비어 있는 영상 파일이 없어 바뀐 것이 없습니다.",
      );
    } catch (error) {
      logger.error("dataset video fps designation failed", error);
      toast.error(resolveErrorMessage(error));
      throw error;
    }
  }

  const currentDatasetUuid = currentDataset?.uuid ?? null;

  return {
    datasetUuid,
    currentUserRole,
    dataset,
    datasetDetails,
    structure,
    currentDataset,
    // 보관·복원과 정보(이름·담당자·행동 주체) 수정은 이 Dataset 의 생성자·admin 만 할 수 있다.
    // 서버가 행마다 산출한 can_write 를 그대로 쓴다. 설정 저장 자격은 리뷰 바인딩의 permissions 가
    // 따로 가진다.
    canArchive: currentDataset?.can_write === true,
    archiving,
    isCurrentDatasetBookmarked:
      currentDatasetUuid !== null && includesDatasetUuid(bookmarkUuids, currentDatasetUuid),
    isCurrentDatasetBeingBookmarked:
      currentDatasetUuid !== null && includesDatasetUuid(addingDatasetUuids, currentDatasetUuid),
    // 데이터셋 리뷰 바인딩과 그 진입점. 별도 창을 열지 않고 같은 창의 리뷰 탭으로 전환한다.
    // 탭 상태는 호스트가 소유한다. 그래서 요청만 호스트에 전달한다.
    review,
    openReview: () => options.onRequestTab?.("metadata"),
    // 어노테이션 스튜디오 바인딩. 어노테이션 탭 본문과 창 닫기 가드(dirty)가 읽는다.
    annotationStudio,
    // 저장 위치가 Annotation 을 지원하는지. null 은 Dataset 응답을 아직 받지 못했다는 뜻이다. 그동안
    // 탭 본문은 스튜디오 대신 대기나 실패를 그린다.
    annotationSupported,
    // Annotation 을 지원하지 않는 저장 위치. 값이 있으면 탭 본문이 스튜디오 대신 잠금 카드를 그린다.
    annotationUnsupportedBackend:
      annotationSupported === false ? (knownStorageBackend?.backend ?? null) : null,
    // 리뷰 액션 결과. 공통 처리(요청·확정 뒤 상세 재조회)만 여기서 한다. 창 닫기처럼 호스트마다
    // 다른 후속 동작은 options 로 전달한다. 요청 뒤에도 다시 조회한다. pair 의 revision 이 올라가서
    // 후보가 화면에 표시되어야 하기 때문이다.
    onReviewOutcome: (outcome: DatasetReviewOutcome) => {
      setRefreshKey((current) => current + 1);
      options.onReviewOutcome?.(outcome);
    },
    // 리뷰의 fps 지정. 설정 축과 독립이다. 그래서 draft 를 거치지 않고 즉시 반영된다.
    onDesignateVideoFps: designateVideoFps,
    // archived 복구(Activate). 라벨 없는 단순 확인 모달.
    restoreConfirmOpen,
    restoring,
    closeRestoreConfirm: () => setRestoreConfirmOpen(false),
    confirmRestore: () => void confirmRestore(),
    onAddBookmarkClick: (target: Dataset) => void handleAddBookmarkClick(target),
    onArchiveToggleClick: (target: Dataset) => void handleArchiveToggleClick(target),
    onDatasetDetailsRefresh: handleDatasetDetailsRefresh,
    onDatasetChanged: () => setRefreshKey((current) => current + 1),
  };
}

export type DatasetDetailController = ReturnType<typeof useDatasetDetailController>;

async function loadDataset(datasetUuid: string): Promise<Dataset | null> {
  return getDataset(datasetUuid);
}
